// Sürücü Puantajı — "Puantaj Kayıtları" düzenleme ekranı.
// Snapshot fact sheet'i tarih aralığı/sürücü filtresiyle listeler; satırda sürücü
// değiştirme, km/süre düzeltme, sil/geri al; üstte elle kayıt ekleme ve (admin)
// belirli bir tarihi yeniden snapshot'lama.

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AuthContext } from "shared/context/auth-context";
import { StoreContext } from "shared/context/store-context";
import Button from "shared/components/FormElements/Button";
import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";

import useDriverPerformanceApi from "../hooks/useDriverPerformanceApi";
import { filterWorklogRows, loadStoredFilters, saveSharedDriverFilters } from "../performanceFilters";
import { KIND_META, addDays, formatKm, formatMinutes, longDate, todayYMD, weekdayName } from "../utils";
import WorklogAddModal from "./WorklogAddModal";
import { canRunWorklogSnapshot } from "./worklogAccess";

import "../DriverPerformance.css";
import "./WorklogEditor.css";

const initialYesterday = () => addDays(todayYMD(), -1);

const WorklogEditor = () => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const store = useContext(StoreContext);
  const showSnapshotControls = useMemo(() => canRunWorklogSnapshot(auth), [auth]);
  const driverOptions = store?.driverOptions || [];
  const { isLoading, listWorklogs, updateWorklog, removeWorklog, restoreWorklog, runWorklogSnapshot, createWorklog } =
    useDriverPerformanceApi();

  const yesterday = addDays(todayYMD(), -1);

  const clampToYesterday = useCallback(
    (ymd) => {
      if (!ymd) return yesterday;
      return ymd > yesterday ? yesterday : ymd;
    },
    [yesterday],
  );

  const initialSharedFilters = useMemo(() => loadStoredFilters(), []);

  const [start, setStart] = useState(() => addDays(initialYesterday(), -6));
  const [end, setEnd] = useState(initialYesterday);
  const [activeDay, setActiveDay] = useState(initialYesterday);
  const [nameQuery, setNameQuery] = useState(initialSharedFilters.nameQuery);
  const [metricsMissingOnly, setMetricsMissingOnly] = useState(initialSharedFilters.metricsMissingOnly);
  const [includeRemoved, setIncludeRemoved] = useState(false);
  const [rows, setRows] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [snapshotDate, setSnapshotDate] = useState(initialYesterday);
  const [snapshotForce, setSnapshotForce] = useState(false);

  const driverNameById = useMemo(() => {
    const m = new Map();
    driverOptions.forEach((d) => m.set(String(d.value), d.label));
    return m;
  }, [driverOptions]);

  const clampActiveDay = useCallback(
    (day, rangeStart, rangeEnd) => {
      let rs = clampToYesterday(rangeStart);
      let re = clampToYesterday(rangeEnd);
      if (rs > re) {
        const tmp = rs;
        rs = re;
        re = tmp;
      }
      const d = clampToYesterday(day);
      if (d < rs) return rs;
      if (d > re) return re;
      return d;
    },
    [clampToYesterday],
  );

  useEffect(() => {
    saveSharedDriverFilters({ nameQuery, metricsMissingOnly });
  }, [nameQuery, metricsMissingOnly]);

  useEffect(() => {
    setStart((prev) => clampToYesterday(prev));
    setEnd((prev) => clampToYesterday(prev));
    setSnapshotDate((prev) => clampToYesterday(prev));
  }, [clampToYesterday]);

  useEffect(() => {
    setActiveDay((prev) => clampActiveDay(prev, start, end));
  }, [start, end, clampActiveDay]);

  useEffect(() => {
    setEditingId(null);
    setEditValues({});
  }, [activeDay]);

  const dayPager = useMemo(() => {
    let rs = clampToYesterday(start);
    let re = clampToYesterday(end);
    if (rs > re) {
      const tmp = rs;
      rs = re;
      re = tmp;
    }
    let total = 0;
    let index = 1;
    for (let d = rs; d <= re; d = addDays(d, 1)) {
      total += 1;
      if (d === activeDay) index = total;
    }
    return {
      rangeStart: rs,
      rangeEnd: re,
      index,
      total,
      canPrev: activeDay > rs,
      canNext: activeDay < re,
    };
  }, [start, end, activeDay, clampToYesterday]);

  const fetchRows = useCallback(async () => {
    setErrorMessage(null);
    try {
      const result = await listWorklogs({
        start: activeDay,
        end: activeDay,
        includeRemoved,
      });
      setRows(result?.worklogs || []);
    } catch (err) {
      setErrorMessage(err?.message || t("driverPerformance.worklog.fetchError"));
    }
  }, [listWorklogs, activeDay, includeRemoved, t]);

  const filterOpts = useMemo(() => ({ nameQuery, metricsMissingOnly }), [nameQuery, metricsMissingOnly]);

  const filteredRows = useMemo(() => filterWorklogRows(rows, filterOpts), [rows, filterOpts]);

  const hasActiveFilters = !!(nameQuery.trim() || metricsMissingOnly);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditValues({ driverId: row.driverId, km: row.km, minutes: row.minutes });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const saveEdit = async (row) => {
    setBusyId(row.id);
    try {
      const patch = {};
      if (editValues.driverId && editValues.driverId !== row.driverId) patch.driverId = editValues.driverId;
      if (editValues.km !== null && Number(editValues.km) !== row.km) patch.km = Number(editValues.km);
      if (editValues.minutes !== null && Number(editValues.minutes) !== row.minutes)
        patch.minutes = Number(editValues.minutes);
      if (Object.keys(patch).length > 0) {
        await updateWorklog(row.id, patch);
        window?.toast?.success?.(t("driverPerformance.worklog.saved"));
        await fetchRows();
      }
      cancelEdit();
    } catch (err) {
      window?.toast?.error?.(err?.message || t("driverPerformance.worklog.saveFailed"));
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (row) => {
    setBusyId(row.id);
    try {
      await removeWorklog(row.id);
      window?.toast?.success?.(t("driverPerformance.worklog.removed"));
      await fetchRows();
    } catch (err) {
      window?.toast?.error?.(err?.message || t("driverPerformance.worklog.saveFailed"));
    } finally {
      setBusyId(null);
    }
  };

  const handleRestore = async (row) => {
    setBusyId(row.id);
    try {
      await restoreWorklog(row.id);
      window?.toast?.success?.(t("driverPerformance.worklog.restored"));
      await fetchRows();
    } catch (err) {
      window?.toast?.error?.(err?.message || t("driverPerformance.worklog.saveFailed"));
    } finally {
      setBusyId(null);
    }
  };

  const handleSnapshot = async () => {
    try {
      const res = await runWorklogSnapshot(snapshotDate, snapshotForce);
      if (res?.skipped) {
        window?.toast?.info?.(t("driverPerformance.worklog.snapshotSkipped"));
      } else {
        window?.toast?.success?.(t("driverPerformance.worklog.snapshotDone", { count: res?.inserted ?? 0 }));
      }
      await fetchRows();
    } catch (err) {
      window?.toast?.error?.(err?.message || t("driverPerformance.worklog.saveFailed"));
    }
  };

  const handleAdd = async (body) => {
    await createWorklog(body);
    window?.toast?.success?.(t("driverPerformance.worklog.added"));
    setAddOpen(false);
    const day = body.date ? clampActiveDay(body.date, start, end) : activeDay;
    if (day !== activeDay) {
      setActiveDay(day);
    } else {
      await fetchRows();
    }
  };

  const goPrevDay = () => {
    if (dayPager.canPrev) setActiveDay(addDays(activeDay, -1));
  };

  const goNextDay = () => {
    if (dayPager.canNext) setActiveDay(addDays(activeDay, 1));
  };

  const handleRangeStartChange = (value) => {
    const v = clampToYesterday(value);
    setStart(v);
    if (v > end) setEnd(v);
  };

  const handleRangeEndChange = (value) => {
    const v = clampToYesterday(value);
    setEnd(v);
    if (v < start) setStart(v);
  };

  return (
    <div className="dp-page">
      <div className="dp-page__header">
        <h2>{t("driverPerformance.worklog.title")}</h2>
        <div className="dp-page__controls">
          <Button type="button" size="small" onClick={() => setAddOpen(true)}>
            {t("driverPerformance.worklog.addRow")}
          </Button>
        </div>
      </div>

      <p className="dp-page__intro">{t("driverPerformance.worklog.intro")}</p>

      <div className="dp-page__filters">
        <input
          type="search"
          className="dp-page__search"
          value={nameQuery}
          onChange={(e) => setNameQuery(e.target.value)}
          placeholder={t("driverPerformance.filter.searchDriver")}
          aria-label={t("driverPerformance.filter.searchDriver")}
        />
        <label className="dp-page__filter-check">
          <input
            type="checkbox"
            checked={metricsMissingOnly}
            onChange={(e) => setMetricsMissingOnly(e.target.checked)}
          />
          {t("driverPerformance.filter.metricsMissingOnly")}
        </label>
      </div>

      <div className="wl-filters">
        <label>
          {t("driverPerformance.worklog.start")}:
          <input
            type="date"
            className="daily-schedule__date-input"
            value={start}
            max={end <= yesterday ? end : yesterday}
            onChange={(e) => handleRangeStartChange(e.target.value)}
          />
        </label>
        <label>
          {t("driverPerformance.worklog.end")}:
          <input
            type="date"
            className="daily-schedule__date-input"
            value={end}
            min={start}
            max={yesterday}
            onChange={(e) => handleRangeEndChange(e.target.value)}
          />
        </label>
        <label className="wl-checkbox">
          <input type="checkbox" checked={includeRemoved} onChange={(e) => setIncludeRemoved(e.target.checked)} />
          {t("driverPerformance.worklog.showRemoved")}
        </label>
      </div>

      {showSnapshotControls && (
      <div className="wl-snapshot">
        <span className="wl-snapshot__label">{t("driverPerformance.worklog.snapshotTitle")}:</span>
        <input
          type="date"
          className="daily-schedule__date-input"
          value={snapshotDate}
          max={yesterday}
          onChange={(e) => setSnapshotDate(clampToYesterday(e.target.value))}
        />
        <label className="wl-checkbox">
          <input type="checkbox" checked={snapshotForce} onChange={(e) => setSnapshotForce(e.target.checked)} />
          {t("driverPerformance.worklog.force")}
        </label>
        <Button type="button" size="small" inverse onClick={handleSnapshot}>
          {t("driverPerformance.worklog.runSnapshot")}
        </Button>
      </div>
      )}

      {errorMessage && <div className="dp-page__error">{errorMessage}</div>}

      <div className="wl-day-pager">
        <Button type="button" size="small" inverse disabled={!dayPager.canPrev} onClick={goPrevDay}>
          {t("driverPerformance.worklog.prevDay")}
        </Button>
        <div className="wl-day-pager__center">
          <input
            type="date"
            className="daily-schedule__date-input wl-day-pager__date"
            value={activeDay}
            min={dayPager.rangeStart}
            max={dayPager.rangeEnd <= yesterday ? dayPager.rangeEnd : yesterday}
            onChange={(e) => setActiveDay(clampActiveDay(e.target.value || activeDay, start, end))}
            aria-label={t("driverPerformance.worklog.date")}
          />
          <span className="wl-day-pager__label">
            <strong>{longDate(activeDay)}</strong>
            <span className="wl-day-pager__weekday">{weekdayName(activeDay)}</span>
          </span>
          <span className="wl-day-pager__meta">
            {t("driverPerformance.worklog.dayPagerPosition", { index: dayPager.index, total: dayPager.total })}
            {" · "}
            {hasActiveFilters && filteredRows.length !== rows.length
              ? t("driverPerformance.worklog.dayRecordCountFiltered", {
                  filtered: filteredRows.length,
                  total: rows.length,
                })
              : t("driverPerformance.worklog.dayRecordCount", { count: filteredRows.length })}
          </span>
        </div>
        <Button type="button" size="small" inverse disabled={!dayPager.canNext} onClick={goNextDay}>
          {t("driverPerformance.worklog.nextDay")}
        </Button>
      </div>

      <div className="dp-table-wrap">
        <table className="dp-table wl-table">
          <thead>
            <tr>
              <th>{t("driverPerformance.table.driver")}</th>
              <th>{t("driverPerformance.worklog.service")}</th>
              <th>{t("driverPerformance.worklog.kind")}</th>
              <th>{t("driverPerformance.worklog.time")}</th>
              <th className="dp-table__num">{t("driverPerformance.worklog.km")}</th>
              <th className="dp-table__num">{t("driverPerformance.worklog.minutes")}</th>
              <th>{t("driverPerformance.worklog.flags")}</th>
              <th>{t("driverPerformance.worklog.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => {
              const editing = editingId === row.id;
              const meta = KIND_META[row.kind];
              const removed = row.status === "REMOVED";
              return (
                <tr key={row.id} className={removed ? "wl-row--removed" : ""}>
                  <td>
                    {editing ? (
                      <select
                        className="wl-select"
                        value={editValues.driverId}
                        onChange={(e) => setEditValues((v) => ({ ...v, driverId: e.target.value }))}
                      >
                        {!driverNameById.has(String(row.driverId)) && (
                          <option value={row.driverId}>{row.driverName}</option>
                        )}
                        {driverOptions.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span>{row.driverName || driverNameById.get(String(row.driverId)) || "-"}</span>
                    )}
                  </td>
                  <td>{row.serviceName}</td>
                  <td>
                    <span
                      className="dp-service-row__kind"
                      style={{ backgroundColor: meta?.bg || "#bbb", borderColor: meta?.border || "#999" }}
                    >
                      {t(meta?.labelKey || "driverPerformance.kind.STANDARD_PERSONEL")}
                    </span>
                  </td>
                  <td>{row.startTime && row.endTime ? `${row.startTime} → ${row.endTime}` : "--:-- → --:--"}</td>
                  <td className="dp-table__num">
                    {editing ? (
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        className="wl-num-input"
                        value={editValues.km}
                        onChange={(e) => setEditValues((v) => ({ ...v, km: e.target.value }))}
                      />
                    ) : (
                      formatKm(row.km)
                    )}
                  </td>
                  <td className="dp-table__num">
                    {editing ? (
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="wl-num-input"
                        value={editValues.minutes}
                        onChange={(e) => setEditValues((v) => ({ ...v, minutes: e.target.value }))}
                      />
                    ) : (
                      formatMinutes(row.minutes)
                    )}
                  </td>
                  <td>
                    {row.source === "MANUAL" && (
                      <span className="dp-tag dp-tag--warn">{t("driverPerformance.worklog.manual")}</span>
                    )}
                    {row.edited && row.source !== "MANUAL" && (
                      <span className="dp-tag dp-tag--inactive">{t("driverPerformance.worklog.edited")}</span>
                    )}
                    {row.metricsMissing && (
                      <span className="dp-tag dp-tag--warn" title={t("driverPerformance.metricsMissingHint")}>
                        {t("driverPerformance.metricsMissing")}
                      </span>
                    )}
                    {removed && (
                      <span className="dp-tag dp-tag--inactive">{t("driverPerformance.worklog.removedTag")}</span>
                    )}
                  </td>
                  <td className="wl-actions">
                    {editing ? (
                      <>
                        <Button type="button" size="small" disabled={busyId === row.id} onClick={() => saveEdit(row)}>
                          {t("driverPerformance.worklog.save")}
                        </Button>
                        <Button type="button" size="small" inverse onClick={cancelEdit}>
                          {t("driverPerformance.worklog.cancel")}
                        </Button>
                      </>
                    ) : removed ? (
                      <Button
                        type="button"
                        size="small"
                        disabled={busyId === row.id}
                        onClick={() => handleRestore(row)}
                      >
                        {t("driverPerformance.worklog.restore")}
                      </Button>
                    ) : (
                      <>
                        <Button
                          type="button"
                          size="small"
                          inverse
                          disabled={busyId === row.id}
                          onClick={() => startEdit(row)}
                        >
                          {t("driverPerformance.worklog.edit")}
                        </Button>
                        <Button
                          type="button"
                          size="small"
                          danger
                          disabled={busyId === row.id}
                          onClick={() => handleRemove(row)}
                        >
                          {t("driverPerformance.worklog.delete")}
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {filteredRows.length === 0 && !isLoading && (
              <tr>
                <td colSpan={8} className="dp-table__empty">
                  {rows.length > 0 && hasActiveFilters
                    ? t("driverPerformance.worklog.emptyFiltered")
                    : t("driverPerformance.worklog.emptyDay")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isLoading && <LoadingSpinner asOverlay />}

      <WorklogAddModal
        show={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={handleAdd}
        driverOptions={driverOptions}
        maxDate={yesterday}
        defaultDate={activeDay}
      />
    </div>
  );
};

export default WorklogEditor;
